import { MenuItem, Icon, Flex, Box, Divider } from '@chakra-ui/react';
import { CheckIcon } from '@heroicons/react/solid';
import { KText } from '@/ui-library/components/text';
import { KMenuButton } from '@/ui-library/components/atoms/KMenuButton/KMenuButton';
import { Radio } from '@kula-ai/components';

export interface Option {
  label: string;
  id: number | string;
}

interface MenuSelectProps {
  prefix?: string;
  options: Option[];
  selected: Option;
  onSelect: (option: Option) => void;
  editMode?: boolean;
  headerText?: string;
  menuStyles?: any;
  isPortal?: boolean;
}

export const MenuSelect = ({
  prefix = null,
  options = [],
  selected,
  onSelect = () => {},
  editMode = false,
  headerText = 'Select an option',
  menuStyles = {},
  isPortal = false,
}: MenuSelectProps) => {
  const triggerContent = (
    <Flex alignItems="center">
      {prefix && (
        <KText mr="4px" fontSize="14px">
          {prefix}:
        </KText>
      )}
      <KText color="secondary-700" fontSize="14px" fontWeight="semiBold">
        {selected?.label}
      </KText>
    </Flex>
  );

  const normalMenuContent = options?.map((option) => {
    const isSelected = option?.id === selected?.id;
    return (
      <MenuItem key={option?.id} onClick={() => onSelect(option)}>
        <Flex alignItems="center" width="100%">
          <KText flex="1" size="sm-medium">
            {option.label}
          </KText>
          {isSelected && <Icon as={CheckIcon} color="#0043CE" />}
        </Flex>
      </MenuItem>
    );
  });

  const editMenuContent = (
    <>
      {headerText && (
        <Box
          p="12px"
          borderBottomWidth="1px"
          borderColor="border-color"
          borderTopRadius="6px"
          bg="secondary-25"
          position="sticky"
          top={0}
          zIndex={1}
        >
          <KText color="secondary-900" size="sm-semi-bold">
            {headerText}
          </KText>
        </Box>
      )}
      <Box maxH="350px" overflowY="auto">
        {options?.map((option, index) => {
          const isSelected = option?.id === selected?.id;
          return (
            <Box key={option?.id}>
              <MenuItem onClick={() => onSelect(option)} py={3}>
                <Flex alignItems="center" width="100%">
                  <Radio
                    label={option.label}
                    checked={isSelected}
                    size="sm"
                    labelClassName="text-sm font-medium text-secondary-900"
                  />
                </Flex>
              </MenuItem>
              {index < options.length - 1 && (
                <Divider borderStyle="solid" borderColor="border-color" />
              )}
            </Box>
          );
        })}
      </Box>
    </>
  );

  const menuContent = editMode ? editMenuContent : normalMenuContent;

  return (
    <KMenuButton
      variant="quaternary"
      triggerContent={triggerContent}
      menuContent={menuContent}
      height="34px"
      border="1px solid"
      borderColor="border-color"
      dividerProps={{ display: 'none' }}
      iconProps={{
        color: 'secondary-500',
      }}
      p="0"
      px="12px"
      _hover={{ bg: 'secondary-25' }}
      _active={{ bg: 'secondary-25' }}
      menuContentProps={{
        position: 'relative',
        zIndex: 99,
        p: editMode ? '0' : '12px',
        borderRadius: '6px',
        borderWidth: editMode ? '1px' : undefined,
        borderColor: editMode ? 'border-color' : undefined,
        boxShadow: editMode ? 'drop-shadows-bottom' : undefined,
        maxH: editMode ? '300px' : '250px',
        overflowY: 'auto',
        ...menuStyles,
      }}
      isPortal={isPortal}
    />
  );
};
